export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
export const noise = (amp) => (Math.random() - 0.5) * 2 * amp;

const SPARK_LEN = 20;
const VX_LEN = 34;

// Build the initial simulation state for a freshly selected domain.
export function seedSim(domain) {
  const b = domain.baselines;
  const telemetry = { ...b };
  const sparks = { ph: [], do2: [], temp: [], nutrient: [] };
  for (let i = 0; i < SPARK_LEN; i++) {
    sparks.ph.push(b.ph + noise(0.02));
    sparks.do2.push(b.do2 + noise(0.6));
    sparks.temp.push(b.temp + noise(0.15));
    sparks.nutrient.push(b.nutrient + noise(0.4));
  }
  // Seed a descending Lyapunov series toward equilibrium.
  const vxSeries = [];
  let v = 1.15;
  for (let i = 0; i < VX_LEN; i++) {
    v = Math.max(0.05, v * 0.9 + noise(0.015));
    vxSeries.push({ t: i, vx: Number(v.toFixed(4)) });
  }
  return {
    telemetry,
    sparks,
    vxSeries,
    bt: 0.92,
    fsmIndex: 0,
    tick: VX_LEN,
    threshold: 1.0,
  };
}

// Advance one nominal (non-quarantine) tick.
export function stepSim(prev, domain) {
  const b = domain.baselines;
  const r = domain.ranges;
  const drift = (val, base, amp, range) =>
    clamp(val + noise(amp) + (base - val) * 0.12, range[0], range[1]);

  const telemetry = {
    ph: drift(prev.telemetry.ph, b.ph, 0.02, r.ph),
    do2: drift(prev.telemetry.do2, b.do2, 0.7, r.do2),
    temp: drift(prev.telemetry.temp, b.temp, 0.12, r.temp),
    nutrient: drift(prev.telemetry.nutrient, b.nutrient, 0.35, r.nutrient),
  };

  const push = (arr, val) => [...arr.slice(-(SPARK_LEN - 1)), val];
  const sparks = {
    ph: push(prev.sparks.ph, telemetry.ph),
    do2: push(prev.sparks.do2, telemetry.do2),
    temp: push(prev.sparks.temp, telemetry.temp),
    nutrient: push(prev.sparks.nutrient, telemetry.nutrient),
  };

  const lastVx = prev.vxSeries[prev.vxSeries.length - 1].vx;
  const nextVx = Math.max(0.04, lastVx * 0.9 + noise(0.012));
  const vxSeries = [
    ...prev.vxSeries.slice(-(VX_LEN - 1)),
    { t: prev.tick + 1, vx: Number(nextVx.toFixed(4)) },
  ];

  const bt = clamp(prev.bt + (0.94 - prev.bt) * 0.12 + noise(0.015), 0, 1);

  // Advance FSM stage every 4 ticks up to the final Sustaining stage.
  const fsmIndex =
    prev.fsmIndex < 3 && (prev.tick + 1) % 4 === 0
      ? prev.fsmIndex + 1
      : prev.fsmIndex;

  return { ...prev, telemetry, sparks, vxSeries, bt, fsmIndex, tick: prev.tick + 1 };
}

// Inject a Lyapunov violation: spike V(x) above threshold, freeze telemetry
// to the laminar holding profile, and collapse the recovery budget.
export function injectFault(prev, domain) {
  const spike = Number((prev.threshold + 0.55 + Math.random() * 0.35).toFixed(4));
  const vxSeries = [
    ...prev.vxSeries.slice(-(VX_LEN - 1)),
    { t: prev.tick + 1, vx: spike, fault: true },
  ];
  return {
    sim: {
      ...prev,
      telemetry: { ...domain.baselines }, // laminar safe-holding profile
      vxSeries,
      bt: Number((0.24 + Math.random() * 0.1).toFixed(3)),
      tick: prev.tick + 1,
    },
    spikeValue: spike,
  };
}

// Per-second decay applied to V(x) during the live post-approval recovery ramp.
export const RECOVERY_DECAY = 0.9;

// Advance one live recovery tick (1s cadence): push the supplied decaying V(x)
// value and nudge the metabolic budget back toward its nominal setpoint.
export function recoveryStep(prev, nextVx) {
  const vxSeries = [
    ...prev.vxSeries.slice(-(VX_LEN - 1)),
    { t: prev.tick + 1, vx: Number(nextVx.toFixed(4)) },
  ];
  const bt = clamp(prev.bt + (0.88 - prev.bt) * 0.2 + noise(0.008), 0, 1);
  return { ...prev, vxSeries, bt, tick: prev.tick + 1 };
}

// Plain-language cross-coupling diagnosis for a Lyapunov breach: names the
// multi-variable interaction and the derivative rates responsible.
const COUPLINGS = [
  { pair: "Temperature × Nutrient", a: { sym: "dT/dt", unit: "°C·min⁻¹" }, b: { sym: "dC/dt", unit: "g·L⁻¹·min⁻¹" }, phrase: "Rapid temperature rise combined with a nutrient spike" },
  { pair: "pH × Dissolved O₂", a: { sym: "dpH/dt", unit: "min⁻¹" }, b: { sym: "dDO/dt", unit: "%·min⁻¹" }, phrase: "A pH drift combined with an oxygen swing" },
  { pair: "Dissolved O₂ × Temperature", a: { sym: "dDO/dt", unit: "%·min⁻¹" }, b: { sym: "dT/dt", unit: "°C·min⁻¹" }, phrase: "An oxygen swing combined with a temperature rise" },
  { pair: "Nutrient × pH", a: { sym: "dC/dt", unit: "g·L⁻¹·min⁻¹" }, b: { sym: "dpH/dt", unit: "min⁻¹" }, phrase: "A nutrient spike combined with a pH drift" },
];

export function buildCrossCoupling(spikeValue) {
  const c = COUPLINGS[Math.floor(Math.random() * COUPLINGS.length)];
  const rand = (lo, hi) => lo + Math.random() * (hi - lo);
  return {
    pair: c.pair,
    rates: [
      { sym: c.a.sym, value: `+${rand(0.4, 0.9).toFixed(2)}`, unit: c.a.unit },
      { sym: c.b.sym, value: `+${rand(2.5, 4.2).toFixed(2)}`, unit: c.b.unit },
    ],
    coupledRate: Number(spikeValue.toFixed(2)),
    envelope: 1.0,
    message: `${c.phrase} exceeded the coupled rate envelope — individual readings nominal, cross-coupling unstable.`,
  };
}

// Estimate seconds for V(x) to descend below the threshold under the holding
// profile. Matches the live recovery ramp: V(x) decays RECOVERY_DECAY per 1s.
export function estimateRecovery(spikeValue, threshold = 1.0) {
  let ticks = 0;
  let v = spikeValue;
  while (v >= threshold && ticks < 60) {
    v *= RECOVERY_DECAY;
    ticks += 1;
  }
  return { seconds: Math.max(3, ticks) };
}
